import React, { createContext, useContext, useState, useEffect } from "react";
import { getQueryString } from "@/config/func";
import { flows, projects, getFlow } from "@/services/common";
import userInfo from "@/Components/user";
import { getParameters } from "@/services/mdb";
import { getMaterials } from "@/services/propsApi";
import { useParams, useLocation } from "react-router-dom";

// 定义状态类型
interface AppState {
  showLeft: boolean;
  curmode: any;
  showElementsBar: boolean;
  loading: boolean;
  curFlow: any;
  curProject: any;
  projectList: any;
  curTitle: string;
  flowData: { nodes: any[]; edges: any[] };
  isSaved: boolean;
  changedParams: any;
  materials: any[];
  globle_physical_method: string;
  showParmsPanel: boolean;
  showEdgesParmsPanel: boolean;
  showInputParams: boolean;
  mParams: any;
  showMaterialsData: boolean;
  curPage: any;
  mSource: any;
  calResult: any;
  isDraggable: boolean;
  inputParams: any;
  flowMode: string | "查看流程" | "新建流程" | "编辑流程";
  isHasJob: boolean; // 当前流程中是否包含计算任务
  curTaskInfo: any; // 当前查看的任务信息
  refreshMenu: boolean;
  curJob: any;
  curResultPage: any; //当前显示的结果页面的tab
  viewSettingPage: any; //任务未完成状态时页面的切换
}

interface AppActions {
  loadData: () => void;
  setShowLeft: (value: boolean) => void;
  setCurmode: (value: any) => void;
  setShowElementsBar: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setCurFlow: (value: any) => void;
  setCurProject: (value: any) => void;
  setProjectList: (value: any) => void;
  setCurTitle: (value: string) => void;
  setFlowData: (value: { nodes: any[]; edges: any[] }) => void;
  setIsSaved: (value: boolean) => void;
  setChangedParams: (value: any) => void;
  setMaterials: (value: any[]) => void;
  set_globle_physical_method: (value: string) => void;
  setShowParmsPanel: (value: boolean) => void;
  setShowEdgesParmsPanel: (value: boolean) => void;
  setShowInputParams: (value: boolean) => void;
  setMParams: (value: any) => void;
  setShowMaterialsData: (value: boolean) => void;
  setCurPage: (value: any) => void;
  setMSource: (value: any) => void;
  setIsDraggable: (value: any) => void;
  setCalResult: (value: any) => void;
  setInputParams: (value: any) => void;
  setCurTaskInfo: (value: any) => void;
  setFlowMode: (value: string) => void;
  setIsHasJob: (value: boolean) => void;
  setRefreshMenu: (value: boolean) => void;
  setCurJob: (value: any) => void;
  setCurResultPage: (value: any) => void;
  setViewSettingPage: (value: any) => void;
}

const AppContext = createContext<{
  state: AppState;
  actions: AppActions;
} | null>(null);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  // 初始化状态
  const [state, setState] = useState<AppState>({
    showLeft: true,
    curmode: "mod_left",
    showElementsBar: false,
    loading: false,
    curFlow: null,
    curProject: null,
    projectList: null,
    curTitle: "",
    flowData: { nodes: [], edges: [] },
    isSaved: true,
    changedParams: {},
    materials: [],
    globle_physical_method: "SRK",
    showParmsPanel: false,
    showEdgesParmsPanel: false,
    showInputParams: false,
    mParams: null,
    showMaterialsData: false,
    curPage: {},
    mSource: null, //所有物质
    isDraggable: true,
    calResult: false,
    inputParams: false,
    flowMode: "编辑流程",
    isHasJob: true,
    curTaskInfo: null,
    refreshMenu: true,
    curJob: null,
    curResultPage: null,
    viewSettingPage: null,
  });

  const params = useParams(); // 获取路由路径参数（k/f）
  const location = useLocation(); // 监听路由变化

  const getRouteParam = (paramName: string) => {
    const pathname = location.pathname;
    const match = pathname.match(/\/project\/(\d+)\/flow\/(\d+)/);
    if (match) {
      const [_, k, f] = match;
      if (paramName === "k") return k;
      if (paramName === "f") return f;
    }
    return null;
  };

  // 定义操作
  const actions: AppActions = {
    loadData: async () => {
      setState((prev) => ({ ...prev, loading: true }));
      const pid = params.k || getQueryString("k") || getRouteParam("k");
      const fid = params.f || getQueryString("f") || getRouteParam("f");

      // const pid = getQueryString("k");
      // const fid = getQueryString("f");

      // 只有当k和f都存在时才执行查询
      if (pid && fid) {
        try {
          const curF = await queryflow(pid, fid);
          if (curF) {
            setState((prev) => ({ ...prev, curFlow: curF.data }));
            const job_count = curF.data.job_count || 0;
            setState((prev) => ({ ...prev, isHasJob: job_count > 0 }));
            setState((prev) => ({
              ...prev,
              flowMode: job_count > 0 ? "查看流程" : "编辑流程",
              curPage:
                job_count > 0
                  ? {
                    label: "计算任务管理",
                    key: "results",
                  }
                  : {
                    label: "全局设置",
                    key: "settingMaterial",
                  },
            }));
            loadProjects(pid, curF.data);
          }
        } catch (error) {
          console.error("加载流程数据失败:", error);
          setState((prev) => ({ ...prev, loading: false }));
        }
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },

    setShowLeft: (value) => setState((prev) => ({ ...prev, showLeft: value })),
    setCurmode: (value) => setState((prev) => ({ ...prev, curmode: value })),
    setShowElementsBar: (value) => setState((prev) => ({ ...prev, showElementsBar: value })),
    setLoading: (value) => setState((prev) => ({ ...prev, loading: value })),
    setCurFlow: (value) => setState((prev) => ({ ...prev, curFlow: value })),
    setCurProject: (value) => setState((prev) => ({ ...prev, curProject: value })),
    setProjectList: (value) => setState((prev) => ({ ...prev, projectList: value })),
    setCurTitle: (value) => setState((prev) => ({ ...prev, curTitle: value })),
    // setCurTitle: (value) =>
    //   setState(prev => ({ ...prev, curTitle: value, curProject: { ...prev.curProject, title: value } })),
    setFlowData: (value) => setState((prev) => ({ ...prev, flowData: value })),
    setIsSaved: (value) => setState((prev) => ({ ...prev, isSaved: value })),
    setChangedParams: (value) => setState((prev) => ({ ...prev, changedParams: value })),
    setMaterials: (value) => setState((prev) => ({ ...prev, materials: value })),
    set_globle_physical_method: (value) =>
      setState((prev) => ({ ...prev, globle_physical_method: value })),
    setShowParmsPanel: (value) => setState((prev) => ({ ...prev, showParmsPanel: value })),
    setShowEdgesParmsPanel: (value) =>
      setState((prev) => ({ ...prev, showEdgesParmsPanel: value })),
    setShowInputParams: (value) => setState((prev) => ({ ...prev, showInputParams: value })),
    setMParams: (value) => setState((prev) => ({ ...prev, mParams: value })),
    setShowMaterialsData: (value) => setState((prev) => ({ ...prev, showMaterialsData: value })),
    setCurPage: (value) => setState((prev) => ({ ...prev, curPage: value })),
    setMSource: (value) => setState((prev) => ({ ...prev, mSource: value })),
    setIsDraggable: (value) => setState((prev) => ({ ...prev, isDraggable: value })),
    setCalResult: (value) => setState((prev) => ({ ...prev, calResult: value })),
    setInputParams: (value) => setState((prev) => ({ ...prev, inputParams: value })),
    setFlowMode: (value) => setState((prev) => ({ ...prev, flowMode: value })),
    setIsHasJob: (value) => setState((prev) => ({ ...prev, isHasJob: value })),
    setCurTaskInfo: (value) => setState((prev) => ({ ...prev, curTaskInfo: value })),
    setRefreshMenu: (value) => setState((prev) => ({ ...prev, refreshMenu: value })),
    setCurJob: (value) => setState((prev) => ({ ...prev, curJob: value })),
    setCurResultPage: (value) => setState((prev) => ({ ...prev, curResultPage: value })),
    setViewSettingPage: (value) => setState((prev) => ({ ...prev, viewSettingPage: value })),
  };

  // 初始化数据
  useEffect(() => {
    actions.loadData();
  }, [params.k, params.f, location.search]);
  //  }, [params.k, params.f, location.search, location.pathname, getQueryString("j")]);
  // useEffect(() => {
  //   // setState((prev) => ({
  //   //   ...prev,
  //   //   curPage: { label: "全局设置", key: "settingMaterial" },
  //   // }));
  //   actions.loadData();
  // }, [getQueryString("k"), getQueryString("f"), getQueryString("j")]);
  const loadProjects = (pid: any, flow: any) => {
  const ps = getProjects();
  ps.then((r) => {
    let p: any = [];
    r.data.data.map((item: any, index: any) => {
      p[index] = {
        ...item,
        flows: [],
      };
      // 只加载当前项目的流程
      if (Number(pid) === item.id) {
        const fl: any = getFlows(item.id);
        fl.then((oneFlow: any) => {
          p[index]["flows"] = [...oneFlow.data.data];
          setState((prev) => ({
            ...prev,
            curProject: p[index],
            curTitle: `${flow.name}`,
          }));
        });
      }
    });
    setState((prev) => ({ ...prev, projectList: p, loading: false }));
  });
};

  const getProjects = async () => {
    return await projects(
      {
        owner_id: userInfo.id,
        page: 1,
        perPage: 1000,
      },
      userInfo.access_token
    );
  };

  const getFlows = async (pid: any) => {
    console.log("调用全局 的getFlowa");
    return flows(
      pid,
      {
        page: 1,
        perPage: 1000,
      },
      userInfo.access_token
    );
  };

  const queryflow = async (pid: any, flow_id: any) => {
    return await getFlow(pid, flow_id, userInfo.access_token);
  };

  useEffect(() => {
    if (state.curFlow) {
      if (state.curFlow.flow_data_url) {
        fetch(state.curFlow.flow_data_url)
          .then((r) => r.json())
          .then((res) => {
            setState((prev) => ({ ...prev, flowData: { ...res } }));
          });
      } else {
        setState((prev) => ({ ...prev, flowData: { nodes: [], edges: [] } }));
      }
    }
  }, [state.curFlow?.id]);

  // useEffect(() => {
  //   // 初始化materials
  //   // setState(prev => ({ ...prev, materials: buildOptionsData([...]) }));
  //   getMaterials({}).then((r) => {
  //     setState((prev) => ({ ...prev, mSource: r.data.response.data }));
  //   });
  //   // 初始化mParams
  //   getParameters({ page: 1, perPage: 1000 }, userInfo.access_token).then((r) => {
  //     let t: any = {};
  //     r.data.data.map((item: any) => {
  //       t[item.title] = {
  //         id: item.id,
  //         title: item.title,
  //         name: item.name,
  //         description: item.description,
  //         units: item.units,
  //       };
  //     });
  //     setState((prev) => ({ ...prev, mParams: t }));
  //   });
  // }, []);

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
